import { useMutation, useQuery } from '@tanstack/react-query';
import { isSuccess } from '../../common/serviceUtil';
import { getEntry, postEntry, putEntry } from '../../services/predictorService';
import { useEntryStore } from './entryStore';
import { useEventStart } from '../../hooks/eventStart/eventStart';
import { useEventStore } from '../event/eventStore';
import { useCallback, useContext, useMemo } from 'react';
import { Event, Selection } from '../../interfaces/sports';
import { usePredictorStore } from '../predictor/predictorStore';
import { Entry, Pick } from '../../interfaces/predictors';
import { devTools } from '../../common/devtools';
import { UserContext } from '@incentivegames/ig-frontend-common/lib/contexts/userContext';
import { filterOutUndefinedValues } from '../../common/arrayUtils';
import { useAddError, useIsErrorPopupOpen } from '../../contexts/ErrorPopupContext';
import { useConfigStore } from '../options/optionsStore';

interface UseGetEntryProps {
  userToken?: string;
  predictorId?: number;
}

export const useGetEntry = ({ userToken, predictorId }: UseGetEntryProps) => {
  const setEntry = useEntryStore((s) => s.setEntry);
  const refetchTime = useEventStart();
  return useQuery(['getEntry', userToken, predictorId], ({ signal }) => getEntry({ userToken, predictorId, signal }), {
    refetchOnWindowFocus: false,
    onSettled: (data, error) => {
      if (isSuccess(data)) {
        setEntry(data.value);
        return;
      }
    },
    enabled: Boolean(predictorId) && Boolean(userToken),
    //re-fetch data after this many milliseconds, if 0 don't re-fetch
    refetchInterval: refetchTime === 0 ? false : refetchTime,
    refetchOnMount: false,
  });
};

export interface PickedHorse extends Selection {
  eventNumber: number;
  eventName: string;
  raceStartTime: number;
  active?: boolean;
}

export interface AbandonedRace extends Event {
  selectionName: 'abandoned';
  active: true;
  eventNumber: number;
}

export const isPickedHorse = (selection?: Selection | AbandonedRace): selection is PickedHorse =>
  selection?.selectionName !== 'abandoned';

export const isAbandonedRace = (selection?: Selection | AbandonedRace): selection is AbandonedRace =>
  selection?.selectionName === 'abandoned';

export const useGetPickedHorse = (): Array<PickedHorse | AbandonedRace> => {
  const picks = useEntryStore((s) => s?.picks);
  const events = useEventStore((s) => s.events);

  return useMemo(() => {
    if (!events || !picks) return [];
    const pickedHorses = picks.map<PickedHorse | AbandonedRace | undefined>((pick) => {
      const event = events.find((e) => e.eventId === pick.eventId);
      if (!event) {
        console.error(`No event found for event ${pick.eventId}`);
        return undefined;
      }
      const market = event.markets[0];
      if (!market) {
        console.error(`No market found for event ${event.eventId}`);
        return undefined;
      }
      const selection = market.selections.find((p) => p.selectionCode === pick.selectionCode);
      if (!selection) return undefined;
      if (!event.eventActive)
        return {
          ...event,
          selectionName: 'abandoned',
          active: true,
          eventNumber: pick.eventNumber,
        };
      return {
        ...selection,
        eventNumber: pick.eventNumber,
        eventName: event.eventName,
        raceStartTime: event.eventStartTime,
        active: pick.active,
      };
    });

    let filtered = filterOutUndefinedValues(pickedHorses);

    return filtered.sort((a, b) => a.eventNumber - b.eventNumber);
  }, [events, picks]);
};

export const usePlayClickedAction = () => {
  const setPlayClicked = useEntryStore((s) => s.setPlayClicked);
  return (value: boolean) => setPlayClicked(value);
};

const compareEntry = (entry: Entry | undefined, picks: Pick[]) => {
  if (!entry) return true;
  for (const currentPick of entry.picks) {
    const newPick = picks.find((newPick) => newPick.eventId === currentPick.eventId);
    if (newPick?.selectionCode !== currentPick.selectionCode) {
      return true;
    }
  }
  return false;
};

interface UseUpsertEntryProps {
  uniqueId: string;
}

export const useUpsertEntry = ({ uniqueId }: UseUpsertEntryProps) => {
  const {
    user: { token },
  } = useContext(UserContext);
  const addError = useAddError();
  const popupOpen = useIsErrorPopupOpen();
  const countryRegion = useConfigStore((s) => s.countryRegion);
  const countryCode = useConfigStore((s) => s.countryCode);
  const entry = useEntryStore((s) => s.entry);
  const predictorId = usePredictorStore((s) => s.activePredictor?.predictorId);
  const picks = useEntryStore((s) => s.picks);
  const setEntry = useEntryStore((s) => s.setEntry);
  // check if we actually need to make this request
  // post if no entry, put if entry and its changed, check useEntryStore.pick vs useEntryStore.entry.picks
  const mutationFunc = useMemo(() => (entry ? putEntry : postEntry), [entry]);
  devTools.log('useUpsertEntry func', mutationFunc.name);
  //https://tanstack.com/query/v3/docs/react/guides/mutations
  const { mutate, status } = useMutation(
    () =>
      mutationFunc({
        token,
        receipt: entry?.receipt,
        payload: {
          uniqueId,
          predictorId,
          picks: picks ?? [],
          countryRegion,
          countryCode,
        },
      }),
    {
      onSettled: (data) => {
        if (isSuccess(data)) {
          setEntry(data.value);
          return;
        }

        const errorMessage = !entry ? 'selection-not-made-error' : 'selection-not-updated-error';
        const helpMessage = !entry ? 'please-try-again' : 'please-try-again-shortly';

        addError({ errorMessage, helpMessage });
      },
    },
  );

  // return an empty function if no difference between useEntryStore.pick vs useEntryStore.entry.picks
  return useCallback(() => {
    if (popupOpen) return;
    if (process.env.STORYBOOK) return;
    if (!picks || picks.length !== 6) return;

    const val = compareEntry(entry, picks ?? []);
    if (val && status !== 'loading') mutate();
    else devTools.log('no difference between entry and picks');
  }, [popupOpen, entry, mutate, picks, status]);
};
